import { z } from 'zod';

export enum CustomCollectionVisibilityEnum {
  PRIVATE = 'PRIVATE',
  SHARED = 'SHARED',
  TEAM = 'TEAM',
}

export enum CustomCollectionFeatureKeyEnum {
  DOCUMENTS = 'documents',
  DISCUSSIONS = 'discussions',
  NOTES = 'notes',
  // TASKS intentionally omitted — collections link to Projects instead, and a
  // project's own task list is the source of truth. Any legacy
  // settings.features.tasks values stored in the DB are dropped by
  // normalizeCustomCollectionSettings below.
  PROJECTS = 'projects',
  CONTACTS = 'contacts',
  COMMUNICATIONS = 'communications',
}

export enum CustomCollectionRelationshipCardinalityEnum {
  ONE_TO_ONE = 'ONE_TO_ONE',
  ONE_TO_MANY = 'ONE_TO_MANY',
  MANY_TO_ONE = 'MANY_TO_ONE',
  MANY_TO_MANY = 'MANY_TO_MANY',
}

export const CustomCollectionVisibilitySchema = z.nativeEnum(CustomCollectionVisibilityEnum);
export const CustomCollectionFeatureKeySchema = z.nativeEnum(CustomCollectionFeatureKeyEnum);
export const CustomCollectionRelationshipCardinalitySchema = z.nativeEnum(CustomCollectionRelationshipCardinalityEnum);

export const CustomCollectionFeatureConfigSchema = z.object({
  documents: z.boolean().default(true),
  discussions: z.boolean().default(true),
  notes: z.boolean().default(true),
  projects: z.boolean().default(false),
  contacts: z.boolean().default(false),
  communications: z.boolean().default(false),
});

export type CustomCollectionFeatureConfig = z.infer<typeof CustomCollectionFeatureConfigSchema>;

export const CustomCollectionSettingsSchema = z.object({
  features: CustomCollectionFeatureConfigSchema.default({
    documents: true,
    discussions: true,
    notes: true,
    projects: false,
    contacts: false,
    communications: false,
  }),
  // Optional display label for the built-in primary "Name" column. When unset,
  // the column is shown as "Name". The underlying entry field is always `name`.
  // Blank/whitespace input is coerced to undefined so clearing it resets to the
  // default rather than failing validation.
  nameFieldLabel: z.preprocess(
    value =>
      typeof value === 'string' && value.trim() === '' ? undefined : value,
    z.string().trim().min(1).max(60).optional(),
  ),
});

export type CustomCollectionSettings = z.infer<typeof CustomCollectionSettingsSchema>;

export function getDefaultCustomCollectionSettings(): CustomCollectionSettings {
  return CustomCollectionSettingsSchema.parse({});
}

export function normalizeCustomCollectionSettings(
  settings?: Partial<CustomCollectionSettings> | Record<string, unknown> | null,
): CustomCollectionSettings {
  const rawFeatures =
    settings && typeof settings === 'object' && 'features' in settings
      ? (settings as { features?: Record<string, unknown> }).features
      : undefined;

  const rawNameFieldLabel =
    settings && typeof settings === 'object' && 'nameFieldLabel' in settings
      ? (settings as { nameFieldLabel?: unknown }).nameFieldLabel
      : undefined;

  return CustomCollectionSettingsSchema.parse({
    features: {
      ...getDefaultCustomCollectionSettings().features,
      ...(rawFeatures || {}),
    },
    nameFieldLabel:
      typeof rawNameFieldLabel === 'string' && rawNameFieldLabel.trim()
        ? rawNameFieldLabel
        : undefined,
  });
}

// Base schema for CustomCollection
export const CustomCollectionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  creatorId: z.string().uuid().optional().nullable(),
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  description: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  visibility: CustomCollectionVisibilitySchema.default(CustomCollectionVisibilityEnum.TEAM),
  settings: CustomCollectionSettingsSchema.default(getDefaultCustomCollectionSettings()),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type CustomCollection = z.infer<typeof CustomCollectionSchema>;
export type CustomCollectionVisibility = z.infer<typeof CustomCollectionVisibilitySchema>;

// Schema for creating a CustomCollection
export const CreateCustomCollectionDtoSchema = CustomCollectionSchema.omit({
  id: true,
  organizationId: true,
  creatorId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  slug: z.string().min(1).optional(),
  settings: CustomCollectionSettingsSchema.optional(),
});

export type CreateCustomCollectionDto = z.infer<typeof CreateCustomCollectionDtoSchema>;

// Schema for updating a CustomCollection
export const UpdateCustomCollectionDtoSchema = CreateCustomCollectionDtoSchema.partial();

export type UpdateCustomCollectionDto = z.infer<typeof UpdateCustomCollectionDtoSchema>;

// Collection member schemas
export const CustomCollectionMemberSchema = z.object({
  id: z.string().uuid(),
  collectionId: z.string().uuid(),
  userId: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type CustomCollectionMember = z.infer<typeof CustomCollectionMemberSchema>;

export const AddCustomCollectionMemberSchema = z.object({
  userId: z.string().uuid(),
});

export type AddCustomCollectionMember = z.infer<typeof AddCustomCollectionMemberSchema>;

// Base schema for CustomCollectionEntry
export const CustomCollectionEntrySchema = z.object({
  id: z.string().uuid(),
  collectionId: z.string().uuid(),
  name: z.string().min(1, 'Name is required'),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type CustomCollectionEntry = z.infer<typeof CustomCollectionEntrySchema>;

// Schema for creating a CustomCollectionEntry
export const CreateCustomCollectionEntryDtoSchema = CustomCollectionEntrySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type CreateCustomCollectionEntryDto = z.infer<typeof CreateCustomCollectionEntryDtoSchema> & {
  customFields?: Record<string, unknown>;
};

// Schema for updating a CustomCollectionEntry
export const UpdateCustomCollectionEntryDtoSchema = CreateCustomCollectionEntryDtoSchema.partial();

export type UpdateCustomCollectionEntryDto = z.infer<typeof UpdateCustomCollectionEntryDtoSchema> & {
  customFields?: Record<string, unknown>;
};

// Extended type for use in dropdown display
export interface DropdownOption {
  value: string;
  label: string;
}

export const CustomCollectionRelationDefinitionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  sourceCollectionId: z.string().uuid(),
  targetCollectionId: z.string().uuid(),
  key: z.string().min(1),
  sourceLabel: z.string().min(1),
  inverseLabel: z.string().min(1),
  cardinality: CustomCollectionRelationshipCardinalitySchema,
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type CustomCollectionRelationDefinition = z.infer<typeof CustomCollectionRelationDefinitionSchema>;

export const CreateCustomCollectionRelationDefinitionDtoSchema = CustomCollectionRelationDefinitionSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  isActive: z.boolean().optional(),
});

export type CreateCustomCollectionRelationDefinitionDto = z.infer<typeof CreateCustomCollectionRelationDefinitionDtoSchema>;

export const UpdateCustomCollectionRelationDefinitionDtoSchema =
  CreateCustomCollectionRelationDefinitionDtoSchema.partial();

export type UpdateCustomCollectionRelationDefinitionDto =
  z.infer<typeof UpdateCustomCollectionRelationDefinitionDtoSchema>;

export const CustomCollectionRelationEdgeSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  definitionId: z.string().uuid(),
  sourceEntryId: z.string().uuid(),
  targetEntryId: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type CustomCollectionRelationEdge = z.infer<typeof CustomCollectionRelationEdgeSchema>;

export const UpdateCustomCollectionFeaturesDtoSchema = CustomCollectionFeatureConfigSchema.partial();
export type UpdateCustomCollectionFeaturesDto = z.infer<typeof UpdateCustomCollectionFeaturesDtoSchema>;

export const UpdateCustomCollectionRelationshipValuesDtoSchema = z.object({
  targetEntryIds: z.array(z.string().uuid()).default([]),
});

export type UpdateCustomCollectionRelationshipValuesDto =
  z.infer<typeof UpdateCustomCollectionRelationshipValuesDtoSchema>;
